import {
  ApiDataResponse,
  GetAllOrganizationsDto,
  GetAllOrganizationsResponse,
  OrganizationDTO,
  OrganizationEventListDto,
  OrganizationEventListResponse,
  OrganizationInviteDTO,
  OrganizationInviteResponse,
  OrganizationPlatformConnections,
  OrganizationPlatformConnectionsResponse,
  OrganizationResponse,
  OrganizationUserListDto,
  OrganizationUserListResponse,
  PlatformConnectionStatus,
  RoleListDto,
  RoleListResponse,
} from "@root/utils/dtos";
import { ApiTransport } from "./config/apiTransport";
import { Organization } from "@root/utils/entities";
import { Pagination } from "@root/utils/types";

export class OrganizationService {
  private readonly _apiTransport: ApiTransport;

  constructor(sessionCookie?: string) {
    this._apiTransport = new ApiTransport(sessionCookie);
  }

  private normalizePlatformStatus = (status: any) => ({
    isConnected: Boolean(status?.isConnected ?? status?.IsConnected),
    pageId: status?.pageId ?? status?.PageId,
    pageName: status?.pageName ?? status?.PageName,
    organizationUrn: status?.organizationUrn ?? status?.OrganizationUrn,
    organizationName: status?.organizationName ?? status?.OrganizationName,
    needsReconnect: Boolean(status?.needsReconnect ?? status?.NeedsReconnect),
    tokenExpiresAt: status?.tokenExpiresAt ?? status?.TokenExpiresAt ?? null,
    isExpiringSoon: Boolean(status?.isExpiringSoon ?? status?.IsExpiringSoon),
  });

  validatePlatformConnection = (
    organizationId: string,
    platform: "facebook" | "linkedin"
  ): Promise<
    ApiDataResponse<{
      platform: string;
      valid: boolean;
      tokenRevoked: boolean;
      error?: string | null;
      status: PlatformConnectionStatus;
    }>
  > =>
    this._apiTransport
      .post<any>(
        `/api/v1/organizations/${organizationId}/platform-connections/${platform}/validate`,
        {}
      )
      .then((resp: any) => ({
        ...resp,
        data: resp?.data
          ? {
              platform: resp.data.platform ?? platform,
              valid: Boolean(resp.data.valid),
              tokenRevoked: Boolean(resp.data.tokenRevoked),
              error: resp.data.error ?? null,
              status: this.normalizePlatformStatus(resp.data.status),
            }
          : resp?.data,
      }));

  addOrganization = (organization: Organization): OrganizationResponse =>
    this._apiTransport.post<OrganizationDTO>(
      `/api/v1/organizations`,
      organization,
    );

  inviteUser = (
    organizationId: string,
    roleId: string,
    email: string,
  ): Promise<ApiDataResponse<unknown>> =>
    this._apiTransport.post(`/api/v1/organizations/${organizationId}/invites`, {
      roleId,
      email,
    });

  acceptInvite = (
    organizationId: string,
    inviteId: string,
  ): Promise<ApiDataResponse<unknown>> =>
    this._apiTransport.post(
      `/api/v1/organizations/${organizationId}/invites/${inviteId}`,
      null,
    );

  updateOrganization = (organization: Organization): OrganizationResponse =>
    this._apiTransport.put<OrganizationDTO>(
      `/api/v1/organizations/${organization.id}`,
      organization,
    );

  updateOrganizationUser = (
    organizationId: string,
    userId: string,
    roleId: string,
  ): OrganizationResponse =>
    this._apiTransport.put<OrganizationDTO>(
      `/api/v1/organizations/${organizationId}/users/${userId}`,
      { roleId },
    );

  getOrganization = (id: string): OrganizationResponse =>
    this._apiTransport.get<OrganizationDTO>(`/api/v1/organizations/${id}`);

  getOrganizationInvite = (
    id: string,
    inviteId: string,
  ): OrganizationInviteResponse =>
    this._apiTransport.get<OrganizationInviteDTO>(
      `/api/v1/organizations/${id}/invites/${inviteId}`,
    );

  getOrganizationUsers = (id: string): OrganizationUserListResponse =>
    this._apiTransport.get<OrganizationUserListDto>(
      `/api/v1/organizations/${id}/users?includeInvites=true`,
    );

  getOrganizationFollowers = (id: string): OrganizationUserListResponse =>
    this._apiTransport.get<OrganizationUserListDto>(
      `/api/v1/organizations/${id}/followers`,
    );

  getOrganizationEvents = (
    id: string,
    page: number,
  ): OrganizationEventListResponse =>
    this._apiTransport.get<
      OrganizationEventListDto & { pagination: Pagination }
    >(`/api/v1/organizations/${id}/events?page=${page}`);

  getOrganizationRoles = (): RoleListResponse =>
    this._apiTransport.get<RoleListDto>(`/api/v1/organizations/roles`);

  removeUser = (organizationId: string, userId: string) =>
    this._apiTransport.delete(
      `/api/v1/organizations/${organizationId}/users/${userId}`,
    );

  deleteOrganization = (
    organizationId: string,
    isHardDelete: boolean = false,
  ) =>
    this._apiTransport.delete(
      `/api/v1/organizations/${organizationId}?isHardDelete=${isHardDelete}`,
    );

  updateImage = (
    organizationId: string,
    formData: FormData,
  ): OrganizationResponse =>
    this._apiTransport.postMultipart<OrganizationDTO>(
      `/api/v1/organizations/${organizationId}/image`,
      formData,
    );
  getAllOrganizations = (
    page: number = 0,
    pageSize: number = 25,
    q?: string,
  ): GetAllOrganizationsResponse =>
    this._apiTransport.get<GetAllOrganizationsDto>(
      `/api/v1/organizations/list?page=${page}${pageSize !== 25 ? `&pageSize=${pageSize}` : ""}${q ? `&q=${encodeURIComponent(q)}` : ""}`,
    );

  toggleDigifree = (
    organizationId: string,
  ): Promise<ApiDataResponse<{ isSuccess: boolean; newValue: boolean }>> =>
    this._apiTransport.put(
      `/api/v1/organizations/${organizationId}/toggle-digifree`,
      {},
    );

  toggleStripeConnect = (
    organizationId: string,
  ): Promise<
    ApiDataResponse<{
      isSuccess: boolean;
      newValue: boolean;
      status?: string;
      message?: string;
      redirectUrl?: string | null;
    }>
  > =>
    this._apiTransport.put(
      `/api/v1/organizations/${organizationId}/toggle-stripe-connect`,
      {},
    );

  createStripeConnectLoginLink = (
    organizationId: string,
  ): Promise<ApiDataResponse<{ url: string }>> =>
    this._apiTransport.post(
      `/api/v1/organizations/${organizationId}/stripe-connect/login-link`,
      {},
    );

  getPlatformConnections = (
    organizationId: string
  ): OrganizationPlatformConnectionsResponse =>
    this._apiTransport
      .get(`/api/v1/organizations/${organizationId}/platform-connections`)
      .then((resp: any) => ({
        ...resp,
        data: {
          facebook: this.normalizePlatformStatus(resp?.data?.facebook),
          linkedin: this.normalizePlatformStatus(resp?.data?.linkedin),
        },
      }));

  connectFacebook = (organizationId?: string): Promise<ApiDataResponse<{ authorizationUrl: string }>> =>
    this._apiTransport.get(
      organizationId
        ? `/api/v1/facebook-connection/connect?organizationId=${encodeURIComponent(organizationId)}`
        : `/api/v1/facebook-connection/connect`
    );

  disconnectFacebook = (organizationId?: string): Promise<ApiDataResponse<unknown>> =>
    this._apiTransport.post(
      organizationId
        ? `/api/v1/facebook-connection/disconnect?organizationId=${encodeURIComponent(organizationId)}`
        : `/api/v1/facebook-connection/disconnect`,
      null
    );

  connectLinkedIn = (organizationId?: string): Promise<ApiDataResponse<{ authorizationUrl: string }>> =>
    this._apiTransport.get(
      organizationId
        ? `/api/v1/linkedin-connection/connect?organizationId=${encodeURIComponent(organizationId)}`
        : `/api/v1/linkedin-connection/connect`
    );

  disconnectLinkedIn = (organizationId?: string): Promise<ApiDataResponse<unknown>> =>
    this._apiTransport.post(
      organizationId
        ? `/api/v1/linkedin-connection/disconnect?organizationId=${encodeURIComponent(organizationId)}`
        : `/api/v1/linkedin-connection/disconnect`,
      null
    );

  getLinkedInPendingOrganizations = (
    sessionKey: string,
  ): Promise<
    ApiDataResponse<{
      organizationId: string | null;
      organizations: { organizationId: string; name: string }[];
    }>
  > =>
    this._apiTransport.get(
      `/api/v1/linkedin-connection/pending-organizations?sessionKey=${encodeURIComponent(sessionKey)}`,
    );

  selectLinkedInOrganization = (
    sessionKey: string,
    linkedInOrganizationId: string,
  ): Promise<
    ApiDataResponse<{
      organizationId: string;
      organizationName: string;
      warning?: string | null;
    }>
  > =>
    this._apiTransport.post(`/api/v1/linkedin-connection/select-organization`, {
      sessionKey,
      linkedInOrganizationId,
    });

  getMyPlatformConnections = (): Promise<
    ApiDataResponse<OrganizationPlatformConnections>
  > =>
    Promise.all([
      this._apiTransport.get(`/api/v1/facebook-connection/status`),
      this._apiTransport.get(`/api/v1/linkedin-connection/status`),
    ]).then(([fb, li]) => ({
      data: {
        facebook: this.normalizePlatformStatus((fb as any)?.data),
        linkedin: this.normalizePlatformStatus((li as any)?.data),
      },
    }) as any);
}

export const organizationService = new OrganizationService();
